import { AuditAreaKey, AuditSourceKey } from '@myrewards/contracts';
import { lastDaysFirstDay } from '@myrewards/domain';
import type { AuditFilters } from '../../api/admin';
import { organizationDate } from '../../ui';

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(params: URLSearchParams, key: string, max = 200): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value === '' ? undefined : value.slice(0, max);
}

/**
 * The period the log covers (QA-040, APP-044 §33). The Period select always says which one is in
 * use, so the dates are never a blank box to guess at.
 */
export const AUDIT_PERIODS = ['last_7', 'last_30', 'this_year', 'all', 'dates'] as const;
export type AuditPeriod = (typeof AUDIT_PERIODS)[number];

export const PERIOD_WORDS: Readonly<Record<AuditPeriod, string>> = {
  last_7: 'Last 7 days',
  last_30: 'Last 30 days',
  this_year: 'This year',
  all: 'Every entry',
  dates: 'Pick dates',
};

/**
 * "For someone else" (APP-148 D26, R97): every entry; those done on someone's behalf (a delegate's
 * for their lead too); or only what the Stock Admin team did for an Executive or a Sector Lead,
 * which Roles & lists' counts open.
 */
export const FOR_SOMEONE_WORDS = {
  all: 'All entries',
  someone: 'Done for someone else',
  team: 'Done by the Stock Admin team',
} as const;

/** "For": nobody in particular, until one Executive or Sector Lead is chosen. */
export const ANY_FOR = 'Any Executive or Sector Lead';

/** A link to one person's, award's or record's history: it opens on all of it. */
const HISTORY_KEYS = ['person', 'award', 'entityType', 'entityId'] as const;

/** The presets a range of dates can be (R106), in the order the Period select lists them. */
const PRESETS = ['last_7', 'last_30', 'this_year'] as const;

/**
 * The preset whose range these dates are, if any (R106): a start equal to the preset's start,
 * worked out by the same `periodStart`, and no end. Roles & lists' count links send the exact day
 * the server counted from, with no period; on the day it was counted, that's "Last 30 days", and
 * it says so.
 */
function presetOf(
  from: string | undefined,
  to: string | undefined,
  today: string,
): AuditPeriod | null {
  if (from === undefined || to !== undefined || !DATE.test(from)) return null;
  return PRESETS.find((p) => periodStart(p, today) === from) ?? null;
}

/**
 * Which period the address asks for: the one chosen, "Pick dates" included, whatever its dates
 * (R107); with no period, dates that are a preset's range by that preset's name (R106), other
 * dates as "Pick dates"; else every entry for a link to someone's or something's history; else
 * the last 30 days.
 */
export function auditPeriod(
  params: URLSearchParams,
  today: string = organizationDate(new Date().toISOString()),
): AuditPeriod {
  const chosen = params.get('period');
  const known = AUDIT_PERIODS.find((p) => p === chosen);
  const from = text(params, 'from');
  const to = text(params, 'to');
  const dated = [from, to].some((d) => d !== undefined && DATE.test(d));
  if (known !== undefined) return known;
  if (dated) return presetOf(from, to, today) ?? 'dates';
  return HISTORY_KEYS.some((key) => text(params, key) !== undefined) ? 'all' : 'last_30';
}

/**
 * The first day a period covers, counting today; null for every entry or picked dates. The last
 * 7 and 30 days start where Roles & lists' counts start (R97: one rule, in the domain).
 */
export function periodStart(period: AuditPeriod, today: string): string | null {
  switch (period) {
    case 'last_7':
      return lastDaysFirstDay(today, 7);
    case 'last_30':
      return lastDaysFirstDay(today, 30);
    case 'this_year':
      return `${today.slice(0, 4)}-01-01`;
    case 'all':
    case 'dates':
      return null;
  }
}

/**
 * The Audit log filters, read from the address so a view can be shared, reloaded and linked. A
 * period's dates are worked out from the organization's today, so the export matches the view.
 */
export function auditFiltersFrom(
  params: URLSearchParams,
  today: string = organizationDate(new Date().toISOString()),
): AuditFilters {
  const area = AuditAreaKey.safeParse(params.get('area'));
  const source = AuditSourceKey.safeParse(params.get('source'));
  const person = text(params, 'person');
  // Done for one person is done for someone else: the one implies the other (D26).
  const forPerson = text(params, 'forPerson');
  const forOne = forPerson !== undefined && UUID.test(forPerson);
  const forSomeoneElse = forOne || params.get('forSomeoneElse') === 'true';
  // R97: what the Stock Admin team did for someone, and only what one person did as the actor.
  const actedFor = params.get('actedFor') === 'true';
  const actor = text(params, 'actor');
  const period = auditPeriod(params, today);
  const start = periodStart(period, today);
  const from = period === 'dates' ? text(params, 'from') : (start ?? undefined);
  const to = period === 'dates' ? text(params, 'to') : undefined;
  const entityType = text(params, 'entityType', 60);
  const entityId = text(params, 'entityId');
  const award = text(params, 'award', 60);
  const q = text(params, 'q');
  return {
    ...(area.success && { area: area.data }),
    ...(source.success && { source: source.data }),
    ...(person !== undefined && UUID.test(person) && { person }),
    ...(forSomeoneElse && { forSomeoneElse: true }),
    ...(forOne && { forPerson }),
    ...(actedFor && { actedFor: true }),
    ...(actor !== undefined && UUID.test(actor) && { actor }),
    ...(entityType !== undefined && { entityType }),
    ...(entityId !== undefined && { entityId }),
    ...(award !== undefined && { award }),
    ...(q !== undefined && { q }),
    ...(from !== undefined && DATE.test(from) && { from }),
    ...(to !== undefined && DATE.test(to) && { to }),
  };
}

/** Whether anything but the source chip and the period narrows the log. */
export function narrowed(f: AuditFilters): boolean {
  return Object.entries(f).some(
    ([key, value]) => !['source', 'from', 'to'].includes(key) && value !== undefined,
  );
}

/** The Audit log address with some parameters changed (null removes one). */
export function auditHref(params: URLSearchParams, change: Record<string, string | null>): string {
  const next = new URLSearchParams(params);
  // Picking an entry keeps the page; changing what the log holds starts again from page 1.
  if (Object.keys(change).some((key) => key !== 'entry')) {
    next.delete('page');
    next.delete('upTo');
  }
  for (const [key, value] of Object.entries(change))
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  const query = next.toString();
  return query === '' ? '/settings/system/audit-log' : `/settings/system/audit-log?${query}`;
}
